// Parallel HubSpot Forms API submission for the standalone /pages/leasing
// page's "Financing That Fits. Leasing That Works." drawer (id:
// leasing_contact_form, snippets/leasing-drawer-contact.liquid, rendered
// by sections/leasing.liquid). Same form id and mostly the same markup
// as the PDP financing drawer (assets/product-financing-form-hubspot.js),
// but a distinct page/snippet -- that one is scoped to
// snippets/additional-payment-options.liquid only, so it never runs here.
//
// Grouped with the Leasing forms (Full Application / Pre-Approval on the
// leasing-pages-update branch, and the PDP financing drawer on this
// branch) under the same "Leasing Application" property group -- its own
// dedicated HubSpot form, no inquiry_source (neither leasing form uses
// that concept; each is distinguished purely by its own GUID).
//
// Field shape here is actually simpler than the PDP drawer: no plans/
// term selector (this page's drawer skips straight to the contact form),
// no product SKU/title (this page has no product context at all -- it's
// a standalone marketing page, not a PDP), but it does have its own
// Message field the PDP drawer doesn't have.
//
// Like the PDP financing drawer, this one has CUSTOM JS validation
// (assets/leasing-drawer-contact.js) that calls preventDefault() on
// invalid email/phone input, on top of native `required`/`pattern`
// attributes. Its <script> tag (no `defer`) runs synchronously, earlier
// in the page than this one, and registers its 'submit' listener inside
// its own DOMContentLoaded callback. To land a 'submit' listener AFTER
// it -- so event.defaultPrevented below reflects whether that handler
// already blocked the submission -- this script also runs synchronously
// (no `defer` on its tag in snippets/leasing-drawer-contact.liquid) and
// also waits for its own DOMContentLoaded callback, preserving the same
// registration order.
(function () {
  // Routed through the same-origin form relay (Shopify App Proxy ->
  // nella-app-proxy -> Linen Plus Middleware -> HubSpot), not a direct
  // client-side call to api.hsforms.com -- confirmed blocked by ad
  // blockers/tracking protection on a real visitor's browser. See
  // docs/testing/form-relay-strategy.html.
  const RELAY_URL = '/apps/nella-app-login/forms/leasing_drawer';
  const FORM_TOKEN_URL = '/apps/nella-app-login/forms-token';

  document.addEventListener('DOMContentLoaded', function () {
    const formEl = document.getElementById('leasing_contact_form');
    if (!formEl) return;
    if (formEl.dataset.hubspotWired) return;
    formEl.dataset.hubspotWired = 'true';

    // Same first-space split as every other HubSpot integration in this
    // project -- this form also has one combined "Name" field.
    function splitName(fullName) {
      const trimmed = (fullName || '').trim().replace(/\s+/g, ' ');
      if (!trimmed) return { firstName: '', lastName: '' };
      const spaceIndex = trimmed.indexOf(' ');
      if (spaceIndex === -1) return { firstName: trimmed, lastName: '' };
      return { firstName: trimmed.slice(0, spaceIndex), lastName: trimmed.slice(spaceIndex + 1) };
    }

    function buildHubSpotFields() {
      const fields = [];
      const push = (name, value) => {
        const v = (value ?? '').toString().trim();
        if (v) fields.push({ name, value: v });
      };
      const byId = (id) => (document.getElementById(id)?.value || '').toString().trim();
      const { firstName, lastName } = splitName(byId('LeasingContactFormName'));

      push('email', byId('LeasingContactFormEmail'));
      push('firstname', firstName);
      push('lastname', lastName);
      push('phone', byId('LeasingContactFormPhone'));
      push('company', byId('LeasingContactBusinessName'));
      // Reused -- same "free-text inquiry" concept as Contact Us /
      // Product Contact / Locations, no new property needed. Sent
      // directly (not through `push`), same as every other integration
      // sharing this optional-message property (assets/ordersify-bis-form-hubspot.js,
      // assets/product-contact-form-hubspot.js). Confirmed live: an
      // omitted OR explicitly-blank value both leave HubSpot's existing
      // value in place -- its Forms API won't clear a property that
      // already has data either way. A labeled placeholder reliably
      // overwrites instead, and names this form so a shared property
      // never looks like a different form's leftover message.
      fields.push({ name: 'contact_message', value: byId('LeasingContactFormMessage') || 'No message submitted (Leasing Inquiry)' });

      return fields;
    }

    // Fetched up front and refreshed periodically, not at submit time: a
    // valid submission here navigates away immediately (leasing-drawer-contact.js
    // only calls preventDefault() on invalid input), which tears down this
    // script's JS context before a token fetched right then could reliably
    // resolve -- `keepalive` only protects an already-sent request, not a
    // `.then()` waiting to send a second one after it. A pre-fetched token
    // keeps submit time down to that one keepalive request.
    const FORM_TOKEN_REFRESH_MS = 5 * 60 * 1000; // server TTL is 10 min; refresh with margin
    let cachedFormToken = null;

    function fetchFormToken() {
      return fetch(FORM_TOKEN_URL)
        .then((r) => r.json())
        .then((data) => (data && data.token) || null)
        .catch(() => null);
    }

    function refreshFormToken() {
      fetchFormToken().then((token) => { cachedFormToken = token; });
    }

    refreshFormToken();
    setInterval(refreshFormToken, FORM_TOKEN_REFRESH_MS);

    function submitToHubSpot() {
      try {
        const payload = {
          fields: buildHubSpotFields(),
          pageUri: window.location.href,
          pageName: document.title,
        };
        fetch(RELAY_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...payload, formToken: cachedFormToken }),
          keepalive: true,
        }).catch((err) => console.warn('HubSpot relay failed (Shopify submission is unaffected):', err));
      } catch (err) {
        console.warn('HubSpot relay failed (Shopify submission is unaffected):', err);
      }
    }

    formEl.addEventListener('submit', (event) => {
      // leasing-drawer-contact.js's own submit handler runs first
      // (registration order) and calls preventDefault() when its
      // email/phone validation fails -- skip firing HubSpot for a
      // submission that never actually reaches Shopify.
      if (event.defaultPrevented) return;
      submitToHubSpot();
    });
  });
})();
